import React, { useMemo, useState } from 'react';
import DefaultLayout from '@/layout/DefaultLayout';
import Breadcrumb from '@/components/Breadcrumb';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import DynamicTable from '@/components/DynamicTable';
import Pagination from '@/components/Pagination';
import Spinner from '@/components/Spinner';
import {
  DeleteButton,
  EditButton,
  GreenButton,
  PrimaryButton,
  PurpleButton,
} from '@/components/shared/Button';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal';

import { useTranslation } from 'react-i18next';
import { RootState, store } from '@/state';
import {
  deleteSubProjectPersonnel,
  saveSubProjectPersonnelsAsync,
} from '@/state/slices/subProjectMethodSlice';
import { ThunkDispatch, AnyAction } from '@reduxjs/toolkit';
import { t } from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import { PlusCircleIcon } from '@heroicons/react/solid';
import SubProjectPersonnelModal, {
  ModalProps,
} from './SubProjectPersonnelModal';
import { formatCurrency } from '@/utils/helpers';
import { Currency } from '@/enums/Currency';
import {
  SubprojectPersonnel,
  SubProjectPersonnelsPayload,
} from '@/services/SubProjectMethodsService';

function SubProjectPersonnels() {
  const { id } = useParams();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPersonnelModal, setIsPersonnelModal] = useState(false);
  const [initPersonnelData, setInitPersonnelData] =
    useState<SubprojectPersonnel>();
  const [selectedRow, setSelectedRow] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();

  const { data, status } = useSelector(
    (state: RootState) => state.subProjectMethod!
  );
  let projectCurrency = data?.currency ?? Currency.EUR;
  let subProjectId = data?.id;
  let subProjectPersonnels = useMemo(
    () => data?.subProjectPersonnels ?? [],
    [data, status]
  );

  const ActionButtonsCell = ({ row, value }: any) => (
    <div>
      <DeleteButton
        onClick={() => {
          setSelectedRow(value);
          setIsModalVisible(true);
        }}
      >
        {t('SubProjectPersonnels.deleteButton')}
      </DeleteButton>
      <EditButton
        type="button"
        onClick={() => {
          setInitPersonnelData(row.original);
          setIsPersonnelModal(true);
        }}
      >
        {t('SubProjectPersonnels.editButton')}
      </EditButton>
    </div>
  );

  const handleCancelDelete = () => {
    setIsModalVisible(false);
  };

  const handleDeleteConfirmation = () => {
    handleDeleteSubProjectPersonnel.mutate(selectedRow);
    setIsModalVisible(false);
  };

  const columns = useMemo(
    () => [
      {
        Header: t('SubProjectPersonnels.actionsColumn'),
        accessor: 'id',
        Cell: ({ row, cell: { value } }: any) => (
          <ActionButtonsCell value={value} row={row} />
        ),
        Footer: (infos) => {
          return <>Total</>;
        },
      },
      {
        Header: t('SubProjectPersonnels.personnelCostIdColumn'),
        accessor: 'name',
      },
      {
        Header: t('SubProjectPersonnels.expatColumn'),
        accessor: 'expat',
        Footer: (infos) => {
          var total = useMemo(
            () => infos.rows.reduce((sum, row) => row.values.expat + sum, 0),

            [infos.rows]
          );
          return <>{total}</>;
        },
      },
      {
        Header: t('SubProjectPersonnels.localColumn'),
        accessor: 'local',
        Footer: (infos) => {
          var total = useMemo(
            () => infos.rows.reduce((sum, row) => row.values.local + sum, 0),

            [infos.rows]
          );
          return <>{total}</>;
        },
      },
      {
        Header: t('SubProjectPersonnels.sousTraitantColumn'),
        accessor: 'sousTraitant',
        Footer: (infos) => {
          var total = useMemo(
            () =>
              infos.rows.reduce((sum, row) => row.values.sousTraitant + sum, 0),

            [infos.rows]
          );
          return <>{total}</>;
        },
      },
      {
        Header: t('SubProjectPersonnels.descriptionColumn'),
        accessor: 'description',
      },
      {
        Header: t('SubProjectPersonnels.totalColumn'),
        accessor: 'total',
        Footer: (infos) => {
          var total = useMemo(
            () => infos.rows.reduce((sum, row) => row.values.total + sum, 0),

            [infos.rows]
          );
          return <>{total}</>;
        },
      },
      {
        Header: t('SubProjectPersonnels.hourlyRateColumn'),
        accessor: 'hourlyRate',
      },
      {
        Header: t('SubProjectPersonnels.totalPerShiftColumn'),
        accessor: 'totalPerShift',
        Footer: (infos) => {
          var total = useMemo(
            () =>
              infos.rows.reduce(
                (sum, row) => row.values.totalPerShift + sum,
                0
              ),

            [infos.rows]
          );
          return <>{total}</>;
        },
      },
      {
        Header: t('SubProjectPersonnels.totalCostColumn'),
        accessor: 'totalCost',
        Cell: ({ row, cell: { value } }: any) => {
          var currency = row.original['personnelCurrency'];
          return <span> {formatCurrency(value, currency)} </span>;
        },
        Footer: (infos) => {
          var total = useMemo(
            () =>
              infos.rows.reduce(
                (sum, row) =>
                  parseFloat(row.values.totalCost) + parseFloat(sum),
                0
              ),

            [infos.rows]
          );
          return <>{total.toFixed(4)}</>;
        },
      },
      {
        Header: t('SubProjectPersonnels.totalHoursColumn'),
        accessor: 'totalHours',
        Footer: (infos) => {
          var total = useMemo(
            () =>
              infos.rows.reduce((sum, row) => row.values.totalHours + sum, 0),

            [infos.rows]
          );
          return <>{total}</>;
        },
      },
      {
        Header: t('SubProjectPersonnels.exchangeRateColumn'),
        accessor: 'exchangeRate',
        Footer: (infos) => {
          return <></>;
        },
      },
      {
        Header: t('SubProjectPersonnels.exchangeRateColumn'),
        accessor: 'totalToCurrency',
        Cell: ({ row, cell: { value } }: any) => {
          //var currency = row.original['personalCurrency'];
          return <span> {formatCurrency(value, projectCurrency)} </span>;
        },
        Footer: (infos) => {
          var total = useMemo(
            () =>
              infos.rows.reduce(
                (sum, row) =>
                  parseFloat(row.values.totalToCurrency) + parseFloat(sum),
                0
              ),

            [infos.rows]
          );
          return (
            <>
              {Currency[projectCurrency]} {'  '} {total.toFixed(4)}
            </>
          );
        },
      },
    ],
    [t]
  );

  const queryClient = useQueryClient();

  const handleDeleteSubProjectPersonnel = useMutation(
    async (id: string) => {
      await dispatch(deleteSubProjectPersonnel(id));
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'subProjectPersonnels',
          pageIndex,
          pageSize,
          search,
        ]);
      },
    }
  );

  const pageCount = useMemo(() => {
    return Math.ceil((subProjectPersonnels?.length ?? 0) / pageSize);
  }, [pageSize, subProjectPersonnels]);

  const nextPage = () => setPageIndex(pageIndex + 1);
  const previousPage = () => setPageIndex(pageIndex - 1);
  const onPageChange = (newPageIndex: number) => setPageIndex(newPageIndex);
  const onPageSizeChange = (newPageSize: number) => {
    setPageIndex(0);
    setPageSize(newPageSize);
  };

  const handleSavePersonnels = async () => {
    var payload: SubProjectPersonnelsPayload = {
      subProjectPersonnels: subProjectPersonnels.map((sm) => ({
        ...sm,
        id: sm.id?.includes('new') ? null : sm.id,
      })),
      subProjectMethodId: data?.id!,
    };
    const result = await dispatch(saveSubProjectPersonnelsAsync(payload));
  };
  return (
    <div>
      <PrimaryButton
        type="button"
        onClick={() => {
          setInitPersonnelData(undefined);
          setIsPersonnelModal(true);
        }}
        className="mt-5"
      >
        {t('expenses.add')}
      </PrimaryButton>

      {subProjectId && (
        <PurpleButton
          type="button"
          onClick={handleSavePersonnels}
          className="mt-5 py-2.5"
        >
          {t('stepper.save')}
        </PurpleButton>
      )}
      {status === 'loading' ? (
        <div className="flex items-center justify-center">
          <Spinner height="3rem" width="3rem" />
        </div>
      ) : (
        <div className="flex flex-col">
          <DynamicTable
            dataCount={subProjectPersonnels?.length}
            data={subProjectPersonnels ?? []}
            columns={columns}
            setSearch={setSearch}
            exportPdf={false}
            exportExcel={false}
            hideToolbar={true}
          />
          {isModalVisible && (
            <DeleteConfirmationModal
              isOpen={isModalVisible}
              onDelete={handleDeleteConfirmation}
              onCancel={handleCancelDelete}
            />
          )}
          {isPersonnelModal && (
            <SubProjectPersonnelModal
              closeModal={() => setIsPersonnelModal(false)}
              initialData={initPersonnelData as ModalProps}
            />
          )}
          <Pagination
            pageIndex={pageCount === 0 ? -1 : pageIndex}
            pageCount={pageCount}
            pageSize={pageSize}
            onNextPage={nextPage}
            onPreviousPage={previousPage}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </div>
  );
}

export default SubProjectPersonnels;
